'use client';

import {
  Button,
  ButtonGroup,
  Flex,
  Separator,
  Toggle,
  Tooltip,
} from '@/_components';
import { useEffect, useCallback, useContext, type FC } from 'react';
import type { PDFControlsProps } from './PDFControls.types';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { AnnotationColorItem } from './_components/AnnotationColorItem';
import {
  AnnotationColor,
  Mode,
} from '@/_contexts/PreviewProvider/PreviewProvider.types';
import { saveAnnotations } from '@/_api/admin-annotations';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { UserContext } from '@/_contexts/UserProvider';

const COLORS: Array<AnnotationColor> = ['black', 'red', 'green', 'blue'];

export const PDFControls: FC<PDFControlsProps> = () => {
  const { preferences } = useContext(FirmSettingsContext);
  const { isAdvisor, token } = useContext(UserContext);

  const {
    color,
    currentPage,
    pageRotations,
    setColor,
    mode,
    selectedIds,
    setSelectedIds,
    setMode,
    setPageRotations,
    setPreviewAnnotations,
    previewCompanyIdForAnnotations,
    previewExtractedDocumentId,
    previewAnnotations,
    previewDocumentCreatedAt,
    previewDocumentStatus,
  } = useContext(PreviewContext);

  const isAnnotationsEnabled: boolean = Boolean(
    !!preferences?.annotation_enabled &&
      isAdvisor &&
      typeof previewExtractedDocumentId === 'number',
  );

  const isDocumentProcessing: boolean = (() => {
    if (previewDocumentStatus !== 'processing') return false;

    // If document is processing but created more than 2 minutes and 15 seconds ago, don't disable
    if (previewDocumentCreatedAt) {
      const createdAt = new Date(previewDocumentCreatedAt);
      const now = new Date();
      const diffInSeconds = (now.getTime() - createdAt.getTime()) / 1000;
      // After 2 minutes and 15 seconds, we assume processing is stuck and so we'll allow annotations
      const twoMinutesFifteenSeconds = 2 * 60 + 15;

      if (diffInSeconds > twoMinutesFifteenSeconds) {
        return false;
      }
    }

    return true;
  })();

  const handleDeleteSelected = useCallback(async () => {
    if (!selectedIds.size) return;

    const updatedAnnotations = previewAnnotations.filter(
      (a) => !selectedIds.has(a.id),
    );

    await saveAnnotations({
      extractedDocumentId: previewExtractedDocumentId!,
      token,
      companyId: previewCompanyIdForAnnotations!,
      annotations: updatedAnnotations,
    });

    setPreviewAnnotations(updatedAnnotations);
    setSelectedIds(new Set());
  }, [
    previewAnnotations,
    selectedIds,
    setSelectedIds,
    setPreviewAnnotations,
    token,
    previewCompanyIdForAnnotations,
    previewExtractedDocumentId,
  ]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIds.size) {
        handleDeleteSelected();
      }

      if (e.key === 'Escape') {
        setSelectedIds(new Set());
      }
    };

    globalThis.addEventListener('keydown', handleKeyDown);

    return () => globalThis.removeEventListener('keydown', handleKeyDown);
  }, [selectedIds, handleDeleteSelected, setSelectedIds]);

  return (
    <>
      <Flex gap={6} isYCentered={true}>
        {isAnnotationsEnabled && (
          <>
            <ButtonGroup isSameLine={true} isSmallGroup={true}>
              <Tooltip isDisabled={!isDocumentProcessing}>
                <Tooltip.Content>
                  Annotations are disabled while the document is processing
                </Tooltip.Content>
                <Tooltip.Trigger>
                  <Toggle
                    label="Annotations"
                    isChecked={mode !== 'view'}
                    isReverse={true}
                    isDisabled={isDocumentProcessing}
                    onChange={() => {
                      if (isDocumentProcessing) return;
                      const newMode: Mode = mode === 'view' ? 'move' : 'view';

                      setMode(newMode);
                    }}
                  />
                </Tooltip.Trigger>
              </Tooltip>

              {mode !== 'view' && !isDocumentProcessing && (
                <>
                  <Separator isVertical={true} height={24} />
                  <Button
                    size="small"
                    intent={mode === 'move' ? 'primary' : 'secondary'}
                    iconLeft="cursor-click"
                    isIconOnly={true}
                    withTooltip
                    tooltipPlacement="bottom"
                    value="Move annotation"
                    onClick={() => setMode('move')}
                  />
                  <Button
                    size="small"
                    intent={mode === 'check' ? 'primary' : 'secondary'}
                    iconLeft="check-circle"
                    isIconOnly={true}
                    withTooltip
                    tooltipPlacement="bottom"
                    value="Add check mark"
                    onClick={() => setMode('check')}
                  />
                  <Button
                    size="small"
                    intent={mode === 'textbox' ? 'primary' : 'secondary'}
                    iconLeft="text-box"
                    withTooltip
                    tooltipPlacement="bottom"
                    isIconOnly={true}
                    value="Add text"
                    onClick={() => setMode('textbox')}
                  />
                  <Separator isVertical={true} height={24} />
                  <Button
                    size="small"
                    intent="secondary"
                    value={`Delete Selected (${selectedIds.size})`}
                    isIconOnly={true}
                    iconLeft="trash"
                    withTooltip
                    tooltipPlacement="bottom"
                    onClick={handleDeleteSelected}
                    isDisabled={!selectedIds.size}
                  />
                </>
              )}
            </ButtonGroup>
            {mode !== 'view' && !isDocumentProcessing && (
              <Flex gap={4}>
                {COLORS.map((c) => (
                  <AnnotationColorItem
                    key={c}
                    color={c}
                    isSelected={color === c}
                    onClick={() => setColor(c)}
                  />
                ))}
              </Flex>
            )}
          </>
        )}
      </Flex>

      <Button
        intent="white-fill"
        size="small"
        value={`Rotate page ${currentPage} 90°`}
        iconLeft="rotate"
        isIconOnly={true}
        withTooltip
        tooltipPlacement="bottom"
        onClick={() => {
          const currentRotation = pageRotations[currentPage] || 0;
          const newRotation = currentRotation + 90;
          setPageRotations((prev) => ({
            ...prev,
            [currentPage]: newRotation,
          }));
        }}
      />
    </>
  );
};
